"use client";

import { useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import Link from "next/link";
import { useAuthStore } from "@/store/auth.store";
import { cn } from "@/lib/utils";
import { accountSurface } from "@/components/account/account-ui";
import { Heart, LogOut, MapPin, Package, ShieldCheck, Store, User } from "lucide-react";

function getInitials(name: string) {
  return name
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

const NAV_ITEMS = [
  { href: "/account", label: "Siparişlerim", icon: Package, exact: true },
  { href: "/account/profile", label: "Hesap Bilgileri", icon: User },
  { href: "/account/addresses", label: "Adreslerim", icon: MapPin },
  { href: "/account/wishlist", label: "Favorilerim", icon: Heart },
  { href: "/account/dealer-apply", label: "Bayi Başvurusu", icon: Store },
];

export default function AccountLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const { customer, logout, isReady } = useAuthStore();

  useEffect(() => {
    if (!isReady) return;
    if (!customer) {
      router.replace(`/login?redirect=${encodeURIComponent(pathname)}`);
    }
  }, [isReady, customer, pathname, router]);

  if (!isReady || !customer) return null;

  const isActive = (href: string, exact?: boolean) => {
    if (exact) return pathname === href || pathname.startsWith("/account/orders");
    return pathname.startsWith(href);
  };

  const handleLogout = async () => {
    await logout();
    router.push("/");
  };

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-5 sm:px-6 lg:px-8">
      <div className="mb-5 md:hidden">
        <div
          className={cn(accountSurface, "overflow-x-auto rounded-lg border-[var(--es-border-soft)] p-1")}
          style={{ scrollbarWidth: "none" } as React.CSSProperties}
        >
          <div className="flex min-w-max gap-1">
            {NAV_ITEMS.map((item) => {
              const active = isActive(item.href, item.exact);
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={cn(
                    "inline-flex min-h-11 items-center gap-2 rounded-md px-3 text-xs font-bold transition duration-200",
                    active
                      ? "account-primary-action"
                      : "text-[var(--es-text-muted)] hover:bg-[color-mix(in_srgb,var(--sf-foreground)_6%,transparent)] hover:text-[var(--sf-foreground)]"
                  )}
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  {item.label}
                </Link>
              );
            })}
            <button
              onClick={() => void handleLogout()}
              className="inline-flex min-h-11 items-center gap-2 rounded-md px-3 text-xs font-bold text-[var(--es-text-muted)] transition duration-200 hover:bg-[color-mix(in_srgb,var(--sf-foreground)_6%,transparent)] hover:text-[var(--sf-foreground)]"
            >
              <LogOut className="h-4 w-4 shrink-0" />
              Çıkış
            </button>
          </div>
        </div>
      </div>

      <div className="flex items-start gap-6 lg:gap-8">
        <aside
          className={cn(
            accountSurface,
            "sticky hidden w-60 shrink-0 overflow-hidden rounded-lg border-[var(--es-border-soft)] md:flex md:flex-col lg:w-72"
          )}
          style={{
            top: "calc(73px + 2rem)",
          }}
        >
          <div className="border-b border-[var(--es-border-soft)] px-5 py-5">
            <div className="mb-4 flex items-center gap-3">
              <div
                className="flex h-11 w-11 shrink-0 select-none items-center justify-center rounded-md bg-[var(--sf-primary)] text-sm font-bold text-[var(--sf-card-btn-text,#ffffff)]"
              >
                {getInitials(customer.fullName)}
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-bold leading-tight text-[var(--sf-foreground)]">
                  {customer.fullName}
                </p>
                <p className="mt-1 truncate text-xs text-[var(--es-text-muted)]">
                  {customer.email}
                </p>
              </div>
            </div>
            <div className="grid gap-2">
              <div className="flex items-center gap-2 rounded-md bg-[color-mix(in_srgb,var(--sf-accent)_12%,transparent)] px-3 py-2 text-xs font-semibold text-[var(--sf-foreground)]">
                <ShieldCheck className="h-4 w-4 text-[var(--sf-accent)]" />
                Hesap bilgilerinizi güvenle yönetin
              </div>
            </div>
          </div>

          <div className="px-5 pb-2 pt-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--es-text-muted)]">
              Hesabım
            </p>
          </div>

          <nav className="flex-1 space-y-1 px-3 pb-3">
            {NAV_ITEMS.map((item) => {
              const active = isActive(item.href, item.exact);
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={cn(
                    "flex min-h-11 items-center gap-3 rounded-md px-3 text-sm font-bold transition duration-200",
                    active
                      ? "account-primary-action shadow-[var(--es-shadow-sm)]"
                      : "text-[var(--es-text-muted)] hover:bg-[color-mix(in_srgb,var(--sf-foreground)_6%,transparent)] hover:text-[var(--sf-foreground)]"
                  )}
                >
                  <item.icon className={cn("h-4 w-4 shrink-0", active && "text-[var(--sf-card-btn-text,#ffffff)]")} />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="border-t border-[var(--es-border-soft)] px-3 py-3">
            <button
              onClick={() => void handleLogout()}
              className="flex min-h-11 w-full items-center gap-3 rounded-md px-3 text-sm font-bold text-[var(--es-text-muted)] transition duration-200 hover:bg-[color-mix(in_srgb,var(--sf-foreground)_6%,transparent)] hover:text-[var(--sf-foreground)]"
            >
              <LogOut className="h-4 w-4 shrink-0" />
              Çıkış Yap
            </button>
          </div>
        </aside>

        <main className="min-w-0 flex-1">
          {children}
        </main>
      </div>
    </div>
  );
}
